import { Fragment } from "react";
import { Link } from "wouter";
import "./Breadcrumb.css";

export interface BreadcrumbItem {
  label: string;
  /** Omit for the current page — the last item is always rendered as plain
   * text with `aria-current="page"`, never a link to itself. */
  to?: string;
}

/**
 * The one back-link/breadcrumb idiom for every detail page (UX polish wave 3
 * — six hand-rolled `*-detail__back` idioms plus HostDetailPage's own
 * breadcrumb collapsed into this). Every ancestor crumb before the last is a
 * real link one level up the IA (Home/Develop/Operate/Fleet/Govern/Settings,
 * docs/product/ui-information-architecture.md); the last item is always the
 * current page, unlinked, matching the "you are here" semantics of a real
 * breadcrumb trail rather than a single "go back" arrow.
 *
 * Callers must not hand it two crumbs that resolve to the same destination
 * (the fabricated "Fleet / Machines" both linking `/hosts` this replaced on
 * HostDetailPage) — collapse those into one crumb instead (A-10: no crumb
 * that lies about there being a distinct place to land).
 */
export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="breadcrumb__item">
                {item.to && !isLast ? (
                  <Link to={item.to}>{item.label}</Link>
                ) : (
                  <span aria-current={isLast ? "page" : undefined}>{item.label}</span>
                )}
              </li>
              {!isLast && (
                <li className="breadcrumb__sep" aria-hidden="true">
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
